import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useEffect } from 'react';
import {
  Alert,
  AlertContent,
  AlertTitle,
  Fieldset,
  FieldGroup,
  FieldsetActions,
  Form,
  toast
} from '@heroui/react';

import { SubmitButton } from '../buttons';
import { baseOwnerModel } from '@rhino-project/core/utils';
import {
  useBaseOwner,
  useBaseOwnerId,
  useModelShow,
  useModelUpdate,
  useFieldSetErrors,
  useResolver,
  useSchema,
  useDefaultValues
} from '@rhino-project/core/hooks';
import { NetworkParamError } from '@rhino-project/core/lib';
import { useRhinoContext } from '@rhino-project/core';
import { FormProvider } from '@rhino-project/core/components/forms';
import { useForm, type FieldValues } from 'react-hook-form';
import { FieldFile, FieldString } from '../../Field';

// Stable identity so the useSchema/useDefaultValues memos don't rebuild.
const ORG_FIELDS = ['name', 'logo_attachment'];

export const EditOrganizationProfile = () => {
  const { t } = useTranslation();
  // baseOwner is loosely typed ({ id; [key: string]: unknown }); name is read
  // for display here.
  const baseOwner = useBaseOwner() as { name?: string } | null;
  const model = baseOwnerModel();
  const baseOwnerId = useBaseOwnerId();
  const { mutate, isPending, error } = useModelUpdate(model);
  const paramError = error as NetworkParamError | null;
  const { resource: owner } = useModelShow(model, baseOwnerId);
  const { refreshSession } = useRhinoContext();

  // Validation and defaults come from the model's generated schema, which
  // enforces the required-name rule ("Name can't be blank") client-side.
  const schema = useSchema(model, ORG_FIELDS);
  const defaultValues = useDefaultValues(model, ORG_FIELDS);
  const resolver = useResolver(schema);

  const methods = useForm<FieldValues>({
    defaultValues,
    values: owner as FieldValues | undefined,
    mode: 'onBlur',
    resolver
  });
  const {
    handleSubmit,
    setError,
    setFocus,
    formState: { isDirty }
  } = methods;

  const onError = useFieldSetErrors(setError);
  const onSubmit = useCallback(
    // mutate's onError receives the network error at runtime, which carries the
    // `.errors` payload useFieldSetErrors reads; bridge the differing signatures.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (values: Record<string, any>) =>
      mutate(values, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: onError as any,
        onSuccess: () => {
          toast.success(
            t('rhino.frontend.messages.updated_successfully', {
              name: values.name || baseOwner?.name
            })
          );
          // Refresh the org name and logo shown in the base-owner switcher.
          refreshSession();
        }
      }),
    [mutate, onError, t, baseOwner?.name, refreshSession]
  );

  useEffect(() => setFocus('name'), [setFocus]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Fieldset>
          <FieldGroup>
            <FieldString
              path="name"
              label={t('rhino.frontend.messages.name')}
            />
          </FieldGroup>
          <FieldGroup>
            <FieldFile
              path="logo_attachment"
              label={t('rhino.frontend.messages.logo')}
              description={t(
                'rhino.frontend.messages.upload_a_png_or_jpg_image_to_use_as_your_organization_s_logo'
              )}
              accept="image/png,image/jpeg"
              maxFiles={1}
            />
          </FieldGroup>
          {Array.isArray(paramError?.errors) && (
            <Alert status="danger">
              <AlertContent>
                <AlertTitle>{paramError.errors[0]}</AlertTitle>
              </AlertContent>
            </Alert>
          )}
          <FieldsetActions>
            <SubmitButton isPending={isPending} isDisabled={!isDirty}>
              {t('rhino.frontend.messages.update_value', {
                value1: baseOwner?.name
              })}
            </SubmitButton>
          </FieldsetActions>
        </Fieldset>
      </Form>
    </FormProvider>
  );
};
